// The types a partner can be given.
export const referralPartnerTypes = [
  {
    label: 'Promotional',
    value: 'PROMOTIONAL',
  },
  {
    label: 'Ambassador',
    value: 'AMBASSADOR',
  },
];

// Types that still exist in the data but can no longer be chosen. They are kept
// so a partner saved under one reads as its own label rather than a raw enum.
export const retiredReferralPartnerTypes = [
  {
    label: 'None',
    value: 'NONE',
  },
];

// The list filter offers the retired types too: partners stored as NONE are
// exactly the ones an admin has to find in order to give them a real type.
export const referralPartnerTypeFilters = [
  ...referralPartnerTypes,
  ...retiredReferralPartnerTypes,
];

export const academyCommissionTypes = [
  {label: 'Percentage', value: 'PERCENTAGE'},
  {label: 'Fixed Amount', value: 'FIXED'},
];

export const academyCommissionBases = [
  {
    label: 'Gross — commission on the list price',
    value: 'GROSS',
  },
  {
    label: 'Net — commission on what the buyer actually paid',
    value: 'NET',
  },
];

export const OFFICIAL_EMAIL_DOMAINS = ['joindaba.com', 'dabafinance.com'];

export const isOfficialEmail = email =>
  OFFICIAL_EMAIL_DOMAINS.some(domain =>
    String(email || '')
      .trim()
      .toLowerCase()
      .endsWith(`@${domain}`),
  );

/**
 * Mirrors the server-side validation in users-service so the admin gets the
 * same message without a round trip. Returns an error string, or null.
 */
export const validateAcademyCommission = academyCommission => {
  if (!academyCommission?.isEnabled) return null;

  const rates = academyCommission.rates || [];
  if (!rates.length) {
    return 'At least one academy commission rate is required when enabled';
  }

  if (
    academyCommission.basis &&
    !['GROSS', 'NET'].includes(academyCommission.basis)
  ) {
    return 'Academy commission basis must be GROSS or NET';
  }

  const seen = new Set();
  for (const rate of rates) {
    const currency = rate?.currency;
    if (!currency) return 'Each academy commission rate requires a currency';
    if (seen.has(currency)) {
      return `Duplicate academy commission rate for ${currency}`;
    }
    seen.add(currency);

    if (!['PERCENTAGE', 'FIXED'].includes(rate?.type)) {
      return `Academy commission type for ${currency} must be PERCENTAGE or FIXED`;
    }
    const value = Number(rate?.value);
    if (!(value > 0)) {
      return `Academy commission value for ${currency} must be greater than 0`;
    }
    if (rate.type === 'PERCENTAGE' && value > 100) {
      return `Academy commission percentage for ${currency} cannot exceed 100`;
    }
  }

  return null;
};

/** Shapes the form values into `AcademyCommissionInput`, or undefined. */
export const buildAcademyCommissionInput = values => {
  const rates = (values?.academyCommissionRates || [])
    .filter(rate => rate && rate.currency)
    .map(rate => ({
      currency: rate.currency,
      type: rate.type || 'PERCENTAGE',
      value: Number(rate.value ?? 0),
    }));

  const isEnabled = Boolean(values?.academyCommissionEnabled);

  // Nothing configured and nothing enabled — leave the field off entirely.
  if (!isEnabled && !rates.length) return undefined;

  return {
    isEnabled,
    basis: values?.academyCommissionBasis || 'GROSS',
    rates,
  };
};

/** Turns a partner document back into the flat shape the form holds. */
export const academyCommissionToFormValues = academyCommission => ({
  academyCommissionEnabled: Boolean(academyCommission?.isEnabled),
  academyCommissionBasis: academyCommission?.basis || 'GROSS',
  academyCommissionRates: (academyCommission?.rates || []).map(rate => ({
    currency: rate?.currency,
    type: rate?.type || 'PERCENTAGE',
    value: rate?.value,
  })),
});
